/**
 * The mount point, and the order the providers go in.
 *
 * The order is not arbitrary. Appearance is outermost because it writes to
 * `<html>` and everything under it reads the result. Toast is next because
 * anything below may need to say something went wrong. Auth is below that
 * because signing out has to be able to raise a toast. Routing is innermost of
 * the four, so the guard inside it can already see who is signed in.
 *
 * `AppDataProvider` is deliberately *not* here — it lives inside the protected
 * branch of the route tree, so it never fires eight reads at a server that is
 * about to answer 401 to each.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AppearanceProvider } from './context/AppearanceContext';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { UIProvider } from './context/UIContext';
import './fieldnote.css';

const root = document.getElementById('root');
if (root === null) throw new Error('No #root to mount into.');

createRoot(root).render(
  <StrictMode>
    <AppearanceProvider>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <UIProvider>
              <ErrorBoundary full>
                <App />
              </ErrorBoundary>
            </UIProvider>
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </AppearanceProvider>
  </StrictMode>
);
